import React from 'react';
import './PrintableExpenseReport.css'; // Assuming a similar CSS file structure
import { format } from 'date-fns';

// Interfaces based on data structure from report.controller.js getExpenseReport
interface ExpenseReportItem {
  id: number;
  date: string;
  category: string;
  subcategory?: string | null;
  description: string;
  amount: number;
  createdBy: string; // Assuming this is the user's name/identifier
  method?: string | null;
}

interface ExpenseReportData {
  expenses: ExpenseReportItem[];
  totalAmount: number;
  startDate: string; // Need to pass these from the print page component
  endDate: string;   // Need to pass these from the print page component
  categoryFilter?: string; // Optional filter info
}

interface PrintableExpenseReportProps {
  reportData: ExpenseReportData;
}

// Helper to format dates
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return 'N/A';
  try {
    return format(new Date(date), 'dd.MM.yyyy');
  } catch (e) {
    return 'Invalid Date';
  }
};

// Helper to format currency
const formatCurrency = (value: number | null | undefined): string => {
  if (value === undefined || value === null) return '0 ₸';
  return new Intl.NumberFormat('ru-KZ', {
    style: 'currency',
    currency: 'KZT',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const PrintableExpenseReport: React.FC<PrintableExpenseReportProps> = ({ reportData }) => {
  if (!reportData || !reportData.expenses) {
    return <div className="printable-container">Ошибка: Данные для отчета по расходам не найдены.</div>;
  }

  const {
    expenses,
    totalAmount,
    startDate,
    endDate,
    categoryFilter,
  } = reportData;

  return (
    <div className="printable-container expense-report-container">
      <header className="printable-header">
        <h1>Отчет по расходам</h1>
        <p>Период: {formatDate(startDate)} - {formatDate(endDate)}</p>
        {categoryFilter && categoryFilter !== 'all' && <p>Категория: {categoryFilter}</p>}
      </header>

      <section className="summary-section">
        <h2>Сводка по периоду</h2>
        <div className="summary-grid">
          <div><strong>Всего расходов:</strong> {formatCurrency(totalAmount)}</div>
          <div><strong>Количество записей:</strong> {expenses.length}</div>
        </div>
      </section>

      <section className="details-table">
        <h2>Детализация по расходам</h2>
        <table>
          <thead>
            <tr>
              <th>Дата</th>
              <th>Категория</th>
              <th>Подкатегория</th>
              <th>Описание</th>
              <th>Сумма (₸)</th>
              <th>Способ оплаты</th>
              <th>Кем создан</th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((expense) => (
              <tr key={expense.id}>
                <td>{formatDate(expense.date)}</td>
                <td>{expense.category}</td>
                <td>{expense.subcategory || '-'}</td>
                <td>{expense.description}</td>
                <td>{formatCurrency(expense.amount)}</td>
                <td>{expense.method || '-'}</td>
                <td>{expense.createdBy}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <footer className="printable-footer">
        Отчет сформирован: {formatDate(new Date())}
      </footer>
    </div>
  );
};

export default PrintableExpenseReport;
